import {gql} from '@apollo/client';

import {assertAndContinue} from 'Asserts';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import getQueryParameterConfigs from 'insights/parameters/data/getQueryParameterConfigs';

import {UseAnalyticsBlockQuery, UseAnalyticsBlockQueryVariables} from './__generated/UseAnalyticsBlockQuery';

type AnalyticsDashboard = ExtractByTypename<
  UseAnalyticsBlockQuery['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

type AnalyticsQuery = AnalyticsDashboard['analyticsQueries'][number];

export const USE_ANALYTICS_BLOCK_QUERY = gql`
  query UseAnalyticsBlockQuery($analyticsDashboardId: StrictString!)
  @skipAudit
  @engComponent(name: ANALYSIS_TOOL) {
    analyticsDashboard(id: $analyticsDashboardId) {
      ... on AnalyticsDashboardNode {
        id
        name
        parameterConfigs {
          ...AnalyticsParameterConfigGraphQL
        }
        analyticsQueries {
          id
          sqlQuery
          name
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('AnalyticsParameterConfig').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export type AnalyticsQueryInformation = Pick<AnalyticsQuery, 'name' | 'sqlQuery'> &
  Pick<AnalyticsDashboard, 'parameterConfigs'> & {dashboardName: string};

export default function useAnalyticsBlockQuery({
  analyticsDashboardId,
  analyticsQueryId,
}: {
  analyticsDashboardId: string | null;
  analyticsQueryId: string | null;
}): AnalyticsQueryInformation | null {
  const response = useSkippableBenchlingQuery<UseAnalyticsBlockQuery, UseAnalyticsBlockQueryVariables>(
    USE_ANALYTICS_BLOCK_QUERY,
    {
      variables: {analyticsDashboardId: analyticsDashboardId!},
      skip: analyticsDashboardId == null || analyticsQueryId == null,
    }
  );

  if (
    !response ||
    response.type !== QueryResultType.SUCCESS ||
    isInaccessible(response.data.analyticsDashboard)
  ) {
    return null;
  }

  const query = response.data.analyticsDashboard.analyticsQueries.find(
    ({id}: AnalyticsQuery) => id === analyticsQueryId
  );

  if (
    !assertAndContinue(query != null, 'Expected query to exist in dashboard', {
      analyticsQueryId,
      analyticsDashboardId,
    })
  ) {
    return null;
  }

  const configs = getQueryParameterConfigs(
    query!.sqlQuery,
    response.data.analyticsDashboard.parameterConfigs
  );

  return {
    sqlQuery: query!.sqlQuery,
    name: query!.name,
    parameterConfigs: configs,
    dashboardName: response.data.analyticsDashboard.name,
  };
}
